import { THROTTLE_MS } from './constants'
import { ensureStyleInjected } from './style'
import { patchTranslator } from './translatorPatch'
import { createTextScanner } from './textScanner'
import { initBadgeGlobalListeners, scheduleBadgeLayout, createDetachedBadge } from './badges'
import { setOverlayMeta } from './globals'

/**
 * i18n Debug Overlay bootstrap
 * ---------------------------------
 * Dynamically (lazy) loads i18next and a subset of locale JSON, then:
 *  - Patches the translator to wrap produced strings with sentinel markers (see translatorPatch)
 *  - Scans the DOM for hard‑coded / missing / unknown keys and decorates them with badges
 *  - Watches subsequent DOM mutations so late / dynamic content is also annotated
 *  - Provides visual hints: orange (present in en missing in es), red (missing/unknown/hard‑coded), (ALT) prefix for alt/aria
 * Note that we compare the english and spanish locales file only - the thinking is that if we have spanish translations, it's a safe
 * indicator that the translation workflow has been done
 */

export async function initI18nDevOverlay() {
  // Guard: only enable during local development unless expanded later (keeps prod clean)
  // if (process.env.NODE_ENV !== 'development') return // For QA enablement remove this + add feature flag
  try {
    const mod = await import(/* webpackChunkName: "i18n-overlay" */ 'i18next')
    const i18next = mod.default || mod
    // Load app translations and also pull shared common-ui namespace from i18next (best-effort)
    const [enModule, esModule] = await Promise.all([
      import(/* webpackChunkName: "i18n-en" */ '../locales/en/translation.json'),
      import(/* webpackChunkName: "i18n-es" */ '../locales/es/translation.json').catch(() => ({ default: {} })),
    ])
    const enMap = enModule.default || enModule
    const esMap = esModule.default || esModule
    const enCommonMap = loadCommonUiFromI18Next(i18next, 'en')
    const esCommonMap = loadCommonUiFromI18Next(i18next, 'es')
    setupOverlay(i18next, enMap, esMap, enCommonMap, esCommonMap)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[i18n overlay] init failed', e)
  }
}

export const initI18nDebug = initI18nDevOverlay
export default initI18nDevOverlay

// Flatten a nested object to dotted keys and optionally prefix with a namespace
function flattenToDot(obj, prefix) {
  const out = {}
  const walk = (node, base) => {
    if (!node || typeof node !== 'object') return
    Object.keys(node).forEach((k) => {
      const v = node[k]
      const next = base ? `${base}.${k}` : k
      if (v && typeof v === 'object' && !Array.isArray(v)) walk(v, next)
      else if (typeof v === 'string') out[next] = v
    })
  }
  walk(obj, '')
  const ns = prefix ? `${prefix}:` : ''
  const prefixed = {}
  Object.keys(out).forEach((k) => {
    prefixed[`${ns}${k}`] = out[k]
  })
  return prefixed
}

// Pull common-ui from the live i18next instance (if loaded) and flatten as 'common-ui:' keys
function loadCommonUiFromI18Next(i18next, lang) {
  try {
    if (!i18next?.hasResourceBundle || !i18next?.getResourceBundle) return {}
    if (!i18next.hasResourceBundle(lang, 'common-ui')) return {}
    const bundle = i18next.getResourceBundle(lang, 'common-ui') || {}
    return flattenToDot(bundle, 'common-ui')
  } catch (err) {
    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[i18n overlay] loadCommonUiFromI18Next failed', err)
    }
    return {}
  }
}

function setupOverlay(i18next, enMap, esMap, enCommonMap = {}, esCommonMap = {}) {
  if (window.i18nOverlayBootstrapped) return
  window.i18nOverlayBootstrapped = true

  ensureStyleInjected()

  // Build quick‑lookup Sets used by scanner & translator patch
  // Merge app + common-ui maps
  const mergedEn = { ...(enMap || {}), ...(enCommonMap || {}) }
  const mergedEs = { ...(esMap || {}), ...(esCommonMap || {}) }

  const esKeys = new Set(Object.keys(mergedEs))
  const missingInSpanish = new Set(Object.keys(mergedEn).filter((k) => !esKeys.has(k))) // en key missing in es
  const englishValues = new Set(Object.values(mergedEn).filter((v) => typeof v === 'string')) // used to spot hard‑coded strings

  // Build reverse lookup: English value -> Set of keys (to resolve alt sentinel values to keys)
  const enValueToKeys = new Map()
  const enValuePatterns = [] // { regex: RegExp, key: string }
  const buildValueRegex = (tpl) => {
    try {
      if (typeof tpl !== 'string') return null
      const src = String(tpl)
      const re = /\{[^}]*\}/g
      let last = 0
      const parts = []
      let m = re.exec(src)
      while (m) {
        const literal = src.slice(last, m.index)
        parts.push(literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        parts.push('(.+?)')
        last = m.index + m[0].length
        m = re.exec(src)
      }
      const tail = src.slice(last).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      parts.push(tail)
      const pattern = `^${parts.join('')}$`
      return new RegExp(pattern)
    } catch (err) {
      if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[i18n overlay] buildValueRegex failed', err)
      }
      return null
    }
  }
  Object.entries(mergedEn).forEach(([k, v]) => {
    if (typeof v !== 'string') return
    const norm = String(v).trim()
    if (!enValueToKeys.has(norm)) enValueToKeys.set(norm, new Set())
    enValueToKeys.get(norm).add(k)
    // Only generate a regex for values with placeholders; exact literals should not count as templates
    if (/\{[^}]+\}/.test(v)) {
      const rx = buildValueRegex(v)
      if (rx) enValuePatterns.push({ regex: rx, key: k })
    }
  })

  // Expose metadata for other modules (e.g., alt annotator)
  setOverlayMeta({ esKeys, enValueToKeys, enValuePatterns })
  const unknownTKeys = new Set() // populated at runtime when code asks for unknown keys

  patchTranslator(i18next, mergedEn, missingInSpanish, unknownTKeys, schedule)

  const { scan } = createTextScanner(englishValues, unknownTKeys) // returns scan() to re-process DOM
  const throttleMs = THROTTLE_MS
  let scheduled = false

  function schedule() {
    // Debounce / throttle DOM rescans during mutation bursts
    if (scheduled) return
    scheduled = true
    try {
      scheduleBadgeLayout() // opportunistically reflow existing badges early
    } catch (err) {
      if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[i18n overlay] scheduleBadgeLayout failed', err)
      }
    }
    setTimeout(() => {
      scheduled = false
      scan() // perform DOM walk & annotate new/mutated nodes
      scheduleBadgeLayout() // position any new detached badges
    }, throttleMs)
  }

  initBadgeGlobalListeners()
  schedule()

  // Observe DOM changes (adds/removals, text updates, relevant attribute changes) to keep overlay current
  const observer = new MutationObserver(() => {
    schedule() // queue a (throttled) rescan
    scheduleBadgeLayout() // minor immediate layout adjustment for existing badges
  })
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['alt', 'aria-label', 'role'],
  })

  i18next?.on?.('languageChanged', () => {
    schedule()
    scheduleBadgeLayout()
  })

  // Next frame: add detached badges for any missing keys already present (ensures layout stable first)
  requestAnimationFrame(() => {
    document.querySelectorAll('.i18n-key-missing:not([data-i18n-detached])').forEach((wrapper) => {
      const label = wrapper.getAttribute('data-i18n-missing') ?? 'missing'
      createDetachedBadge(wrapper, { label, missing: true })
    })
  })

  // eslint-disable-next-line no-console
  console.info(
    '[i18n overlay] active: orange = en key missing in es (including common-ui); red = unknown/missing/hard-coded; alt text prefixed with (ALT)'
  )
}
